import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';

import { Box, Divider, Type, Alert, Icon, Stack } from 'react-spark';

import Rlab from '../../components/rlab/Rlab';
import { parseQueryString } from '../../components/routing/helpers';
import services from '../../../services';
import { offersSchema } from '../../components/offers-page-schemas';
import offersV2 from '../../components/offers/offers-v2';
import offersV3 from '../../components/offers/offers-v3';
import OffersV3Layout from '../../components/offers/offers-v3/OffersV3Layout';
import OffersV4Layout from '../../components/offers/offers-v4/OffersV4Layout';
import ABTestPage from './Page';
import StarIcon from './StarIcon';
import BellIcon from './BellIcon';
import CreditCardReductionBanner from '../../components/banners/CreditCardReductionBanner';

const { OffersV2Customize, OffersV2Disclaimer, OffersV2Header, OffersV2Help, OffersV2List, OffersV2ExpectationsCard } = offersV2;
const { OffersV3Disclaimer, OffersV3Header, OffersV3SingleOfferTerm, OffersV3Help, OffersV3Customize } = offersV3;
const api = services.CoreAPIInstance;

const CAMPAIGN_CODE = '2026-05-multiTermComparison';

const sharedComponents = {
    header: (firstName, maxOffer, requestedAmountAvailable, customizedOffer) => <Box className="sprk-o-CenteredColumn sprk-u-pvl sprk-u-ptm@m sprk-u-pbm@m">
        <OffersV2Header firstName={firstName} maxOffer={maxOffer} requestedAmountAvailable={requestedAmountAvailable} customizedOffer={customizedOffer}/>
    </Box>,
    specialText: (hasQlDiscount, qlDiscountAmount, isTeamMember, getsCyberMondayDiscount, referral, counterOffer, counterOfferReportChanged, loanCreatedDate) => (
        <Fragment>
            {(hasQlDiscount || getsCyberMondayDiscount) && (
                <Type.BodyFour className="antialiased sprk-u-TextAlign--center sprk-u-man sprk-u-pvs">
                    {hasQlDiscount && (
                        <div
                            className="discount-box"
                            role="alert"
                        >
                            <div className="discount-icon-text-container">
                                <StarIcon width={36} height={36} fill="currentColor" className="discount-icon" />
                                <div className="sprk-u-plm sprk-u-TextAlign--left">
                                    <p className="sprk-b-TypeBodyOne">
                                        Special discount
                                    </p>
                                    <p className="sprk-b-TypeBodyTwo">
                                        Loyalty pays off: As a Rocket Mortgage&reg; client, save {qlDiscountAmount}% on your interest rate. Explore your offers for more details.
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    {getsCyberMondayDiscount && (
                        <span id="genericSubHeading" className="sprk-u-Display--block">
                            Don&rsquo;t wait. 50% off your origination fee ends when Cyber Monday does.
                        </span>
                    )}
                </Type.BodyFour>
            )}

            {/* Referral alert - always shown when referral exists */}
            {referral && (
                <Alert type="success">
                    <Type.DisplaySix>
                        Thank you for coming from {referral}{`${loanCreatedDate ? ', we see that you applied on ' + new Intl.DateTimeFormat('en-US').format(new Date(loanCreatedDate)) : ''}`}
                    </Type.DisplaySix>
                </Alert>
            )}

            {/* Counter offer alerts - always shown when conditions are met */}
            {counterOffer && (
                <Type.BodyFour className="antialiased sprk-u-TextAlign--center sprk-u-man sprk-u-pvs">
                    <div
                        className="warning-box"
                        role="alert"
                    >
                        <div className="warning-icon-text-container" style={{ display: 'flex', alignItems: 'center' }}>
                            <BellIcon width={36} height={36} fill="currentColor" className="warning-icon" />
                            <div className="sprk-u-plm sprk-u-TextAlign--left">
                                <p className="sprk-b-TypeBodyOne">
                                    Please select a new offer
                                </p>
                                <p className="sprk-b-TypeBodyTwo">
                                    Your application details have changed, so your original offer is no longer available.
                                </p>
                            </div>
                        </div>
                    </div>
                </Type.BodyFour>
            )}

            {counterOfferReportChanged && (
                <Type.BodyFour className="antialiased sprk-u-TextAlign--center sprk-u-man sprk-u-pvs">
                    <div
                        className="warning-box"
                        role="alert"
                    >
                        <div className="warning-icon-text-container" style={{ display: 'flex', alignItems: 'center' }}>
                            <BellIcon width={36} height={36} fill="currentColor" className="warning-icon" />
                            <div className="sprk-u-plm sprk-u-TextAlign--left">
                                    <p className="sprk-b-TypeBodyOne">
                                        Your loan terms have changed. Please select a new offer.
                                    </p>
                                    <p className="sprk-b-TypeBodyTwo">
                                        Your credit profile showed a change from the initial soft credit inquiry that was used to generate your loan offers.
                                    </p>
                            </div>
                        </div>
                    </div>
                </Type.BodyFour>
            )}
        </Fragment>
    ),
    expectations: <div className='sprk-u-Display--none@m sprk-u-phh sprk-u-pbl sprk-u-pth'>
        <OffersV2ExpectationsCard
            contentTop={(
                <Type.DisplayFive>
                    What to expect with your loan:
                </Type.DisplayFive>
            )}
            className="sprk-u-mha"
            style={{ boxShadow: 'none' }} />
    </div>,
    bottom: <Box className="sprk-u-ptm sprk-u-pbl sprk-u-mha" style={{ maxWidth: '86rem' }}>
        <OffersV2Help />
        <Divider className="sprk-u-man" />
        <OffersV2Disclaimer />
    </Box>
};
export class OffersPageComponent extends Component {
    static defaultProps = {
        offers: { default: true },
        getsQLDiscount: false,
        getsRLReturningClientDiscount: false,
        isTeamMember: false,
        cardId: '',
        clientStateMinAmount: 0,
        customizedOffer: 0,
        maxOffer: 0,
        minOffer: 0,
        referral: null,
        loanCreatedDate: null,
        counterOffer: false,
        counterOfferReportChanged: false,
        requestedAmountAvailable: false,
    };

    constructor(props) {
        super(props);
        // Guards the post-allocation re-read. shouldReloadOffers stays true in redux
        // for the life of the page, so without this flag any later fetching
        // transition (e.g. the customize-amount flow) would re-trigger the fetch.
        this.didReloadOffers = false;
    }

    static propTypes = {
        cardId: PropTypes.string.isRequired,
        clientStateMinAmount: PropTypes.number,
        customizedOffer: PropTypes.number.isRequired,
        getsQLDiscount: PropTypes.bool.isRequired,
        getsRLReturningClientDiscount: PropTypes.bool.isRequired,
        isTeamMember: PropTypes.bool.isRequired,
        fetchOffers: PropTypes.func.isRequired,
        shouldReloadOffers: PropTypes.bool,
        fetching: PropTypes.bool.isRequired,
        offers: offersSchema,
        maxOffer: PropTypes.number.isRequired,
        minOffer: PropTypes.number,
        referral: PropTypes.string,
        loanCreatedDate: PropTypes.string,
        counterOffer: PropTypes.bool,
        counterOfferReportChanged: PropTypes.bool,
        requestedAmountAvailable: PropTypes.bool,
    };

    componentWillMount() {
        const queryParams = parseQueryString((this.props.location || window.location).search, window.localStorage);
        const { id, iat, token, partnerExternalReference, partnerCustomerId } = queryParams;
        // if we need to update a partnerExternalReference on selectOffer, we need the partnerCustomerId to verify the referral matches
        if (partnerExternalReference && partnerCustomerId) {
            window.localStorage.setItem('partnerExternalReference', partnerExternalReference);
            window.localStorage.setItem('partnerCustomerId', partnerCustomerId);
        }
        const fetchParams = id && iat ? { id, iat } : token ? { token } : {};
        this.props.fetchOffers(fetchParams);
        self.scrollHandler = () => {
            const elem = document.getElementsByClassName('top-anchor')[0];
            if (!elem) return;
            if (window.scrollY > 500) {
                elem.style.display = 'block';
            } else {
                elem.style.display = 'none';
            }
        }
        document.addEventListener('scroll', self.scrollHandler);
    }

componentDidMount() {
    this.doScrollToTop();

    // Store the current history length to detect back/forward navigation
    this.historyLength = window.history.length;

    // Add pageshow event listener for back button navigation
    window.addEventListener('pageshow', this.handlePageShow);
}

    /**
     * Allocation to the offers-page campaign is deliberately triggered only after
     * the client has seen offers, so the offers already in state were fetched
     * while this loan was still treated as a non-experiment loan. When allocation
     * reports a brand new assignment, fetch offers once more so the experiment
     * view gets its full pool.
     */
    componentDidUpdate() {
        const { shouldReloadOffers, fetchOffers, fetching } = this.props;

        if (!shouldReloadOffers || this.didReloadOffers || fetching) {
            return;
        }

        this.didReloadOffers = true;
        // Deliberately no params. The initial request already established the session
        // cookie, so a bare offers read resolves it - whereas repeating id/iat would
        // burn another inquiry-token use, and repeating a token would re-enter the
        // app-router poll, which can navigate away instead of refreshing offers.
        fetchOffers({});
    }

    doScrollToTop() {
        // Scroll to the top of the page when the component is mounted
        // before scrolling to the top
        // This is to prevent the scroll restoration from interfering with our manual scroll
        let previousScrollRestoration = window.history.scrollRestoration;
        if (previousScrollRestoration) {
            window.history.scrollRestoration = 'manual';
        }
        this.scrollToTopWithRetry(0, () => {
            // Reset scroll restoration to the previous value
            setTimeout(() => {
                if (previousScrollRestoration) {
                    window.history.scrollRestoration = previousScrollRestoration;
                }
            }, 1800);
        });
    }

    // Scroll to top with retry mechanism to handle dynamic content loading
    scrollToTopWithRetry = (attempts = 0, cb = ()=>{}) => {
        const maxAttempts = 3;
        const delay = 300; // ms

        window.scrollTo({
            top: 0,
            behavior: 'smooth',
        });

        // Check if we're actually at the top after scrolling
        setTimeout(() => {
            if (window.pageYOffset > 0 && attempts < maxAttempts) {
                // If not at the top and we haven't exceeded max attempts, try again
                this.scrollToTopWithRetry(attempts + 1, cb);
            } else {
                cb();
            }
        }, delay);
    }

componentWillUnmount() {
    document.removeEventListener('scroll', self.scrollHandler);
    window.removeEventListener('pageshow', this.handlePageShow);
}

// Handle pageshow event (fires on back/forward navigation)
handlePageShow = (event) => {
    // Check if this is a back-forward navigation
    const isBackForwardNavigation = event.persisted ||
                                   window.history.length !== this.historyLength;

    if (isBackForwardNavigation) {
        this.doScrollToTop();
    }

    // Update history length
    this.historyLength = window.history.length;
}

    render() {
        const {
            cardId,
            customizedOffer,
            clientStateMinAmount,
            getsQLDiscount,
            getsRLReturningClientDiscount,
            isTeamMember,
            getsCyberMondayDiscount,
            autoPayDiscountConfig,
            maxOffer,
            minOffer,
            user,
            referral,
            offers,
            loanCreatedDate,
            counterOffer,
            counterOfferReportChanged,
            requestedAmountAvailable
        } = this.props;

        const qlDiscountOffer = (offers && offers.sortedOffers || []).find(offer => !!offer.qlDiscount) || {};
        const qlDiscountAmount = (qlDiscountOffer.qlDiscount || 0).toString();
        // having the discount requires the amount
        const hasQlDiscount = getsQLDiscount && !!qlDiscountAmount;

        // no restriction card c display options.
        const maxOptionsDisplayed = cardId === 'C1' ? Infinity : 6;
        const { firstName = '' } = user || {};
        let customizedOfferLow = this.props.customizedOffer;
        if (offers && offers.sortedOffers && offers.sortedOffers.length) {
            customizedOfferLow = offers.sortedOffers[0].amount;
        }
        return (
            <ABTestPage
                id="offersPage"
                loading={this.props.fetching}
                heading=""
                // Applied so the default container class is not used
                className=""
                padding="flush"
                api={api}
                rlabPage="offersPage">
                <Rlab
                    campaignCode={CAMPAIGN_CODE}
                    components={
                        {
                            control: <Fragment>
                                <CreditCardReductionBanner />
                                {sharedComponents.header(firstName, maxOffer, requestedAmountAvailable, customizedOffer)}
                                <div className='sprk-u-pvm sprk-u-BackgroundColor--gray'
                                    // Used for our web-ui authenticated offers page /app#/loan-options/
                                    style={{ width: '100vw', marginLeft: 'calc(50% - 50vw)'}}>
                                    <Box className="sprk-o-CenteredColumn">
                                        {sharedComponents.specialText(hasQlDiscount, qlDiscountAmount, isTeamMember, getsCyberMondayDiscount, referral, counterOffer, counterOfferReportChanged, loanCreatedDate)}
                                        <Type.DisplayFive className='sprk-u-mbn'>
                                            { requestedAmountAvailable ? 'Your Requested Offer' : 'Your Top Offers'}
                                        </Type.DisplayFive>
                                        <OffersV2List descending maxOffers={maxOptionsDisplayed} hasQlDiscount={hasQlDiscount} qlDiscountAmount={qlDiscountAmount} autoPayDiscountConfig={autoPayDiscountConfig} />
                                    </Box>
                                </div>
                                {/* Customization */}
                                <Box className="sprk-o-CenteredColumn">
                                    <OffersV2Customize
                                        clientStateMinAmount={clientStateMinAmount}
                                        customizedOffer={customizedOffer}
                                        maxOffer={maxOffer}
                                        minOffer={minOffer}
                                        selectionHighToLow={true}
                                        requestedAmountAvailable={requestedAmountAvailable}
                                    />
                                </Box>
                                <div className='sprk-u-BackgroundColor--gray' style={{ width: '100vw', marginLeft: 'calc(50% - 50vw)'}}>
                                    {sharedComponents.expectations}
                                    <Box className="sprk-o-CenteredColumn">
                                        <OffersV2Help />
                                    </Box>
                                </div>
                                <div className='sprk-u-BackgroundColor--white'>
                                    <Box className="sprk-o-CenteredColumn">
                                        <Divider className="sprk-u-man" />
                                        <OffersV2Disclaimer />
                                    </Box>
                                </div>
                                <div
                                    className="top-anchor"
                                    onClick={() => window.scrollTo({
                                        top: document.getElementById('customize-offers').offsetTop - document.getElementsByClassName('sprk-c-Masthead')[0].offsetHeight,
                                        behavior: 'smooth'
                                    })}>
                                </div>
                            </Fragment>,
                            experiment: <OffersV4Layout
                                firstName={firstName}
                                maxOffer={maxOffer}
                                minOffer={minOffer}
                                customizedOffer={customizedOffer}
                                counterOffer={counterOffer}
                                counterOfferReportChanged={counterOfferReportChanged}
                            />,
                        }
                    }
                />
            </ABTestPage >
        );
    }
}

export default OffersPageComponent;
